/** Default payload shapes aligned with IRAS Form C-S API (sampleRequest.txt). */

import {
  formatFormCSValidationErrors,
  normalizeFormCSValidationErrors,
} from "./formCSValidation";
import { isFormCSBasisPeriodLongerThan12Months } from "./formCSBasisPeriodUtils";
import { SIGNED_AMOUNT_FIELDS } from "./formCSDataFormFields";

export const STEPS = ["Filing information", "Form C-S data", "Declaration"];

export const createInitialFilingInfo = (company, authState) => ({
  uen: company?.uen || "",
  uenType: (() => {
    const uen = company?.uen || "";
    // Local Companies: 4 digits + 5 digits + alphabet
    if (/^\d{4}\d{5}[A-Z]$/i.test(uen)) return 0;
    // Foreign Companies: T + 2 digits + 2 alphabets + 4 digits + alphabet
    if (/^T\d{2}[A-Z]{2}\d{4}[A-Z]$/i.test(uen)) return 1;
    // Others (not matching above and not empty)
    if (uen) return 2;
    // Fallback to Local by default if blank
    return 0;
  })(),
  ya: new Date().getFullYear(),
  designationOfFiler: "ACCOUNTANT",
  nameOfContactPerson: authState?.name || "",
  contactNumber: authState?.phoneNumber || "",
  designationOfContactPerson: "ACCOUNTANT",
  declarantRefNo: "",
});

export const IRAS_DECLARATION_KEYS = [
  "isRevBelow5M",
  "isOnlyIncTxbl17",
  "isNotClaimingSpecificItems",
  "isFullAndTrueAccOfCompInc",
  "isQualifiedToUseConvFormCS",
];

const MANDATORY_IRAS_DECLARATION_KEYS = IRAS_DECLARATION_KEYS.filter(
  (key) => key !== "isQualifiedToUseConvFormCS"
);

export const createInitialDeclaration = () => ({
  isRevBelow5M: false,
  isOnlyIncTxbl17: false,
  isNotClaimingSpecificItems: false,
  isFullAndTrueAccOfCompInc: false,
  isQualifiedToUseConvFormCS: false,
});

/** Internal acknowledgement & signature (not sent to IRAS). */
export const createInitialDeclarationAck = () => ({
  submitterCapacity: "",
  taxAgentUen: "",
  isTaxAgentAuthorised: false,
  isAuthorisedAccurate: false,
  declarantName: "",
  declarationDate: "",
  hasConfirmedAllDeclarations: false,
});

export const isValidFormCSUen = (value) =>
  /^[0-9A-Z]{9,10}$/.test(
    String(value || "")
      .trim()
      .toUpperCase()
  );

export const isDeclarationStepValid = (declaration, declarationAck) => {
  const irasOk = MANDATORY_IRAS_DECLARATION_KEYS.every((key) =>
    Boolean(declaration[key])
  );
  const ack = declarationAck || {};
  return (
    irasOk &&
    ["TAXPAYER", "TAX_AGENT"].includes(ack.submitterCapacity) &&
    (ack.submitterCapacity !== "TAX_AGENT" ||
      (isValidFormCSUen(ack.taxAgentUen) && ack.isTaxAgentAuthorised)) &&
    ack.isAuthorisedAccurate &&
    ack.hasConfirmedAllDeclarations &&
    Boolean(ack.declarantName?.trim()) &&
    Boolean(ack.declarationDate?.trim())
  );
};

export const createInitialDataFormCS = () => ({
  sctpInd: 0,
  sctpName: "",
  sctpNo: "",
  basisPeriodFrom: "",
  basisPeriodTo: "",
  fsPrep: 0,
  firstFYE: "",
  prevFYE: "",
  profitLossBeforeTaxation: 0,
  sepSrcIncome: 0,
  receiptNotTxAmt: 0,
  c1_NTDeductibleExp: 0,
  adjPLBefDed: 0,
  renoWorksExpS14Q: 0,
  c1_FurtherDed: 0,
  sgAdjPLAft: 0,
  c1_BC: 0,
  unutilCABFNorm: 0,
  cyCANorm: 0,
  unutilLossBFNorm: 0,
  c1_GrossRent: 0,
  c1_DedExp: 0,
  sgRent: 0,
  sgIntDisc: 0,
  sgOtherI: 0,
  totSgFI: 0,
  unutilDonationBFNorm: 0,
  cyDonation: 0,
  ci: 0,
  unutilCACFNorm: 0,
  unutilLossCFNorm: 0,
  unutilDonationCFNorm: 0,
  totalRevenue: 0,
  grossPL: 0,
  directorFee: 0,
  totRemuneration: 0,
  medicalExp: 0,
  c1_TransportExp: 0,
  c1_EntertainExp: 0,
  inventories: 0,
  tradeReceivables: 0,
  sholderChange: "N",
  uCALDChangePrinAct: "N",
  unutilCALDClaimS23S37: 0,
  fullTxX: "N",
  expRD: 0,
  expRDSG: 0,
  enhanceDeductRD: 0,
  appStockConvAsset: 0,
  c1_EnhancedEISDed: 0,
  eis_ClaimCashPayout: "2",
  eis_ClaimDedAll: "2",
  eis_TrainTotCost: 0,
  eis_TrainDedAll: 0,
  eis_InnoProjTotCost: 0,
  eis_InnoProjDedAll: 0,
  eis_AcqIPRTotCost: 0,
  eis_AcqIPRDedAll: 0,
  eis_LicensIPRTotCost: 0,
  eis_LicensIPRDedAll: 0,
  eis_RegIPTotCost: 0,
  eis_RegIPDedAll: 0,
  eis_RDSgTotCost: 0,
  eis_RDSgDedAll: 0,
  ptisDonInd: "NA",
  foreignAssetsSaleGainLoss: 0,
  foreignSourceSaleGainsRemit: 0,
  foreignSourceSaleNotTax: "NA",
  cyOverseasDonation: 0,
});

const STANDARD_YES_NO_YN_FIELDS = [
  "sholderChange",
  "uCALDChangePrinAct",
  "fullTxX",
];

const PTIS_FOREIGN_YES_NO_FIELDS = ["ptisDonInd", "foreignSourceSaleNotTax"];

const isBlankValue = (value) =>
  value === undefined || value === null || String(value).trim() === "";

const isYesInput = (value) =>
  value === "Y" ||
  value === "1" ||
  value === 1 ||
  value === true ||
  value === "YES";

const isNoInput = (value) => value === "N" || value === "2" || value === 2;

const isPtisDonIndNaInput = (value) => isBlankValue(value) || value === "NA";

const isForeignSourceSaleNotTaxNaInput = (value) =>
  value === "0" || value === 0 || value === "NA";

// Required standard fields no longer support NA; map legacy NA/3 values to No.
const normalizeStandardYesNoForState = (value) => {
  if (isYesInput(value)) {
    return "Y";
  }
  return "N";
};

const normalizeStandardYesNoForPayload = (value) => {
  if (isYesInput(value)) {
    return "1";
  }
  return "2";
};

const normalizePtisForeignYesNoForState = (field, value) => {
  if (isYesInput(value)) {
    return "Y";
  }
  if (isNoInput(value)) {
    return "N";
  }
  if (
    (field === "ptisDonInd" && isPtisDonIndNaInput(value)) ||
    (field === "foreignSourceSaleNotTax" &&
      isForeignSourceSaleNotTaxNaInput(value))
  ) {
    return "NA";
  }
  if (value === "3" || value === 3) {
    return "3";
  }
  return "N";
};

const normalizePtisForeignYesNoForPayload = (field, value) => {
  if (isYesInput(value) || value === "Y") {
    return "1";
  }
  if (isNoInput(value) || value === "N") {
    return "2";
  }
  if (value === "3" || value === 3) {
    return "3";
  }
  if (value === "NA") {
    return field === "ptisDonInd" ? "" : "0";
  }
  if (field === "ptisDonInd" && isBlankValue(value)) {
    return "";
  }
  if (field === "foreignSourceSaleNotTax" && (value === "0" || value === 0)) {
    return "0";
  }
  return "2";
};

const normalizeYesNoFieldForState = (field, value) => {
  if (PTIS_FOREIGN_YES_NO_FIELDS.includes(field)) {
    return normalizePtisForeignYesNoForState(field, value);
  }
  return normalizeStandardYesNoForState(value);
};

const YES_NO_DISPLAY_FIELDS = [
  ...STANDARD_YES_NO_YN_FIELDS,
  ...PTIS_FOREIGN_YES_NO_FIELDS,
];

/** Map Y/N/NA and IRAS payload codes (1/2/3/0) to form state codes for PDF/UI display. */
export const normalizeDataFormFieldValueForDisplay = (field, value) => {
  if (!YES_NO_DISPLAY_FIELDS.includes(field)) {
    return value;
  }
  return normalizeYesNoFieldForState(field, value);
};

const normalizeYesNoFieldForPayload = (field, value) => {
  if (PTIS_FOREIGN_YES_NO_FIELDS.includes(field)) {
    return normalizePtisForeignYesNoForPayload(field, value);
  }
  return normalizeStandardYesNoForPayload(value);
};

const normalizeRequiredYesNoFieldsForState = (dataFormCS = {}) => ({
  ...dataFormCS,
  ...Object.fromEntries(
    [...STANDARD_YES_NO_YN_FIELDS, ...PTIS_FOREIGN_YES_NO_FIELDS].map((key) => [
      key,
      normalizeYesNoFieldForState(key, dataFormCS[key]),
    ])
  ),
});

const normalizeRequiredYesNoFieldsForPayload = (dataFormCS = {}) => ({
  ...dataFormCS,
  ...Object.fromEntries(
    [...STANDARD_YES_NO_YN_FIELDS, ...PTIS_FOREIGN_YES_NO_FIELDS].map((key) => [
      key,
      normalizeYesNoFieldForPayload(key, dataFormCS[key]),
    ])
  ),
});

const trimSctpMembershipNoForPayload = (dataFormCS = {}) => ({
  ...dataFormCS,
  sctpNo:
    dataFormCS.sctpNo == null
      ? dataFormCS.sctpNo
      : String(dataFormCS.sctpNo).trim(),
});

const normalizePositiveAmounts = (dataFormCS = {}) =>
  Object.fromEntries(
    Object.entries(dataFormCS).map(([key, value]) => [
      key,
      typeof value === "number"
        ? SIGNED_AMOUNT_FIELDS.has(key)
          ? Math.round(value)
          : Math.max(0, Math.round(value))
        : value,
    ])
  );

const normalizePrefillDataFormValue = (field, value, currentDataFormCS) => {
  if (value == null || value === "") {
    return value;
  }

  if (typeof currentDataFormCS?.[field] === "number") {
    const numberValue = Number(value);
    return Number.isFinite(numberValue)
      ? numberValue
      : currentDataFormCS[field];
  }

  return value;
};

const normalizePrefillDataFormCS = (irasData = {}, currentDataFormCS = {}) =>
  Object.fromEntries(
    Object.entries(irasData)
      .filter(([field]) =>
        Object.prototype.hasOwnProperty.call(currentDataFormCS, field)
      )
      .map(([field, value]) => [
        field,
        normalizePrefillDataFormValue(field, value, currentDataFormCS),
      ])
  );

/** Map IRAS prefill / saved draft payload into dialog form state. */
export const applyPrefillPayload = (
  merged,
  { filingInfo, dataFormCS, declaration, declarationAck }
) => {
  if (!merged || typeof merged !== "object") {
    return { filingInfo, dataFormCS, declaration, declarationAck };
  }

  const irasData =
    merged.dataFormCS && typeof merged.dataFormCS === "object"
      ? merged.dataFormCS
      : merged.data && typeof merged.data === "object"
        ? merged.data
        : null;

  const nextFilingInfo = merged.filingInfo
    ? {
        ...filingInfo,
        ...merged.filingInfo,
        uenType:
          merged.filingInfo.uenType != null
            ? Number(merged.filingInfo.uenType)
            : filingInfo.uenType,
        ya:
          merged.filingInfo.ya != null
            ? Number(merged.filingInfo.ya)
            : filingInfo.ya,
      }
    : filingInfo;

  const nextDataFormCS = irasData
    ? normalizePositiveAmounts(
        normalizeRequiredYesNoFieldsForState({
          ...dataFormCS,
          ...normalizePrefillDataFormCS(irasData, dataFormCS),
        })
      )
    : dataFormCS;

  const nextDeclaration = merged.declaration
    ? {
        ...declaration,
        ...Object.fromEntries(
          IRAS_DECLARATION_KEYS.map((key) => [
            key,
            Boolean(merged.declaration[key]),
          ])
        ),
      }
    : declaration;

  const ackSource =
    merged.internalDeclarationAck &&
    typeof merged.internalDeclarationAck === "object"
      ? merged.internalDeclarationAck
      : merged.declarationAck && typeof merged.declarationAck === "object"
        ? merged.declarationAck
        : null;

  const nextDeclarationAck = ackSource
    ? { ...declarationAck, ...ackSource }
    : declarationAck;

  return {
    filingInfo: nextFilingInfo,
    dataFormCS: nextDataFormCS,
    declaration: nextDeclaration,
    declarationAck: nextDeclarationAck,
  };
};

const getNestedValue = (source, path) =>
  path.reduce((value, key) => (value == null ? undefined : value[key]), source);

const firstPresentValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const firstDefinedValue = (...values) =>
  values.find((value) => value !== undefined);

const extractIrasErrorBody = (error) =>
  error?.error?.response?.data ??
  error?.response?.data ??
  error?.errors ??
  error?.data ??
  null;

const extractIrasErrorMeta = (body) => ({
  messageCode: firstDefinedValue(
    getNestedValue(body, ["errors", "response", "info", "messageCode"]),
    getNestedValue(body, ["response", "info", "messageCode"]),
    getNestedValue(body, ["error", "response", "info", "messageCode"]),
    getNestedValue(body, ["errors", "messageCode"]),
    getNestedValue(body, ["error", "messageCode"]),
    getNestedValue(body, ["data", "messageCode"]),
    body?.messageCode
  ),
  source: firstPresentValue(
    getNestedValue(body, ["errors", "source"]),
    getNestedValue(body, ["error", "source"]),
    getNestedValue(body, ["data", "source"]),
    body?.source
  ),
});

export const formatIrasApiError = (
  error,
  fallback = "IRAS request failed. Please try again."
) => {
  const body = extractIrasErrorBody(error);
  const message = firstPresentValue(
    getNestedValue(body, ["errors", "response", "info", "message"]),
    getNestedValue(body, ["response", "info", "message"]),
    getNestedValue(body, ["error", "response", "info", "message"]),
    error?.message,
    body?.message,
    getNestedValue(body, ["errors", "message"]),
    fallback
  );
  const { messageCode, source } = extractIrasErrorMeta(body);
  const details = [
    messageCode !== undefined ? `Message code: ${messageCode ?? "-"}` : null,
    source ? `Source: ${source}` : null,
  ].filter(Boolean);

  return details.length ? `${message}\n${details.join("\n")}` : message;
};

export const formatIrasSubmissionError = (error) => {
  const body = extractIrasErrorBody(error);
  const validationErrors =
    body?.errors?.validationErrors ?? body?.validationErrors;

  if (Array.isArray(validationErrors) && validationErrors.length > 0) {
    return formatFormCSValidationErrors(
      normalizeFormCSValidationErrors(validationErrors)
    );
  }

  return formatIrasApiError(
    error,
    "Failed to submit Form C-S. Please try again."
  );
};

const formatPrefillDate = (value, fallback) =>
  value ? `${value}` : fallback || "";

const getCalendarBasisPeriodForYa = (ya) => {
  const year = Number(ya);
  if (!Number.isFinite(year) || year <= 1) {
    return { basisPeriodFrom: "", basisPeriodTo: "" };
  }

  return {
    basisPeriodFrom: `${year - 1}-01-01`,
    basisPeriodTo: `${year - 1}-12-31`,
  };
};

export const buildFormCSPrefillPayload = ({ filingInfo, dataFormCS }) => {
  const calendarBasisPeriod = getCalendarBasisPeriodForYa(filingInfo?.ya);

  return {
    filingInfo: {
      uen: filingInfo?.uen?.trim() || "",
      uenType: "6",
      ya:
        filingInfo?.ya !== undefined && filingInfo?.ya !== null
          ? `${filingInfo.ya}`
          : "",
      declarantRefNo: filingInfo?.declarantRefNo?.trim() || "",
    },
    data: {
      basisPeriodFrom: formatPrefillDate(
        dataFormCS?.basisPeriodFrom,
        calendarBasisPeriod.basisPeriodFrom
      ),
      basisPeriodTo: formatPrefillDate(
        dataFormCS?.basisPeriodTo,
        calendarBasisPeriod.basisPeriodTo
      ),
      fsPrep: dataFormCS?.fsPrep ? `${dataFormCS.fsPrep}` : "",
      firstFYE: formatPrefillDate(dataFormCS?.firstFYE),
      prevFYE: formatPrefillDate(dataFormCS?.prevFYE),
    },
  };
};

export const buildFormCSPayload = ({
  filingInfo,
  declaration,
  dataFormCS,
  declarationAck,
}) => {
  const irasDeclaration = { ...declaration };
  const dataFormCSPayload = normalizePositiveAmounts(
    trimSctpMembershipNoForPayload(
      normalizeRequiredYesNoFieldsForPayload(dataFormCS)
    )
  );
  IRAS_DECLARATION_KEYS.forEach((key) => {
    irasDeclaration[key] = Boolean(declaration[key]);
  });

  const fsPrepValue = Number(dataFormCSPayload.fsPrep);
  if (
    !isFormCSBasisPeriodLongerThan12Months(dataFormCSPayload) &&
    ![1, 2].includes(fsPrepValue)
  ) {
    delete dataFormCSPayload.fsPrep;
    delete dataFormCSPayload.firstFYE;
    delete dataFormCSPayload.prevFYE;
  } else if (fsPrepValue !== 2) {
    if (!dataFormCSPayload.firstFYE) {
      delete dataFormCSPayload.firstFYE;
    }
    delete dataFormCSPayload.prevFYE;
  }

  return {
    filingInfo: {
      ...filingInfo,
      uenType: Number(filingInfo.uenType) || 0,
      ya: Number(filingInfo.ya) || 0,
    },
    declaration: irasDeclaration,
    dataFormCS: dataFormCSPayload,
    ...(declarationAck
      ? {
          internalDeclarationAck: {
            ...declarationAck,
            taxAgentUen:
              declarationAck.submitterCapacity === "TAX_AGENT"
                ? declarationAck.taxAgentUen?.trim().toUpperCase() || ""
                : "",
            declarantName: declarationAck.declarantName?.trim() || "",
            declarationDate: declarationAck.declarationDate?.trim() || "",
          },
        }
      : {}),
  };
};
